import { dateIsInvalid, parseDate } from "@/utils/formatting/date";
import { isValue, type IValue } from "findef";
import type { FormField, FormFieldDefinition, FormFieldReferenceAttributes } from "./field";
import { isNullish } from "@/utils/is";


const NOW = new Date();

type FieldDef = FormFieldDefinition<any, any, any> | FormField<any, any> | FormFieldReferenceAttributes<any, any>; 

const fixBadNumber = (x: number) => {
  if (isNaN(x)) return 0;
  if (isFinite(x) === false) return 0;
  return x;
}

const toBool = (x: any): boolean => {
  if (typeof x === 'undefined' || x === null) return false;
  if (typeof x === 'boolean') return x;
  if (typeof x === 'string') {
    const lower = x.toLowerCase();
    if (lower === 'false' || lower === '0' || lower === 'no' || lower === '') return false;
    if (lower === 'true' || lower === '1' || lower === 'yes') return true;
  }
  return Boolean(x);
}

export const castByType = <ValueType = any>(value: ValueType, vtype?: string): any => {
  switch (vtype) {
    case 'set': {
      if (Array.isArray(value)) {
        return value.filter(it => !isNullish(it));
      }
      return (isNullish(value) || value === '') ? [] : [value];
    };
    case 'dbobject':
    case 'dbObject': {
      if (value === null || value === '') return undefined;
      return value;
    };
    case 'object': {
      if (typeof value === 'object') return value;
      if (typeof value === 'string') {
        if ((value.startsWith('{') && value.endsWith('}')) || (value.startsWith('[') && value.endsWith(']'))) {
          try {
            return JSON.parse(value);
          } catch (e) {
            console.error(e);
          }
        }
      }
      return value;
    };
    case 'array': {
      if (value === '') return [];
      if (Array.isArray(value)) return value;
      if (typeof value === 'undefined' || value === null) return undefined;
      if (typeof value === 'string' && value.includes(',')) return value.split(',');
      return [value];
    };
    case 'value': {
      if (isValue(value)) return value;
      if (typeof value === 'number') {
        const val: IValue = {
          value: value,
          time: NOW,
          percentage: 0,
          __isValue: true
        };
        return val;
      }
      if (typeof value === 'string') {
        const val: IValue = {
          value: Number(value),
          time: NOW,
          percentage: 0,
          __isValue: true
        };
        return val;
      }
      const val: IValue = {
        value: 0,
        time: NOW,
        percentage: 0,
        __isValue: true
      };
      return val;
    } 
    case 'date': {
      const d = parseDate(value)
      if (dateIsInvalid(d)) {
        console.warn(`Invalid date ${d}`);
        return NOW;
      } 
      return d;
    };
    case 'bool':
    case 'boolean': return toBool(value); 
    case 'string':
    case 'email':
    case 'text': {
      if (typeof value === 'undefined' || value === null) return '';
      return (value + '');
    };
    case 'number': {
      if (typeof value === 'number') return fixBadNumber(value);
      if (isValue(value)) return fixBadNumber(value.value ?? 0);
      if (typeof value === 'undefined' || value === null) return 0;
      return fixBadNumber(Number(value));
    };
    default: return value;
  }

}

export const castToInput = <ValueType = any>(value: ValueType, def: FieldDef): any => {
  if (def.type !== 'set' && Array.isArray(value) && value.length === 1) return castByType(value[0], def.type); 
  return castByType(value, def.type);
}

export const castToState = <ValueType = any>(value: ValueType, def: FieldDef): any => {
  return castByType(value, def.transformType || def.type);
}


export const castTypeNameToInput = (name?: string): string | undefined => {
  switch (name) {
    case 'string':
      return 'text';
    default:
      return name;
  }
}
